import { forwardRef, type CSSProperties, type ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { cn } from '@/common/lib/cn/cn'

export interface ScrollGradientBlockProps {
    children: ReactNode
    className?: string
    scrollContainerClassName?: string
    scrollContainerStyle?: CSSProperties
    enabled?: boolean
    fadeDistance?: number
}

const DEFAULT_FADE_DISTANCE = 30

export const ScrollGradientBlock = forwardRef<HTMLDivElement, ScrollGradientBlockProps>(
    (
        {
            children,
            className,
            scrollContainerClassName,
            scrollContainerStyle,
            enabled = true,
            fadeDistance = DEFAULT_FADE_DISTANCE,
        },
        ref
    ) => {
        const scrollContainerRef = useRef<HTMLDivElement>(null)
        const [topGradientOpacity, setTopGradientOpacity] = useState(0)
        const [bottomGradientOpacity, setBottomGradientOpacity] = useState(1)
        const gradientHeight = Math.max(fadeDistance, 1)

        useImperativeHandle(ref, () => scrollContainerRef.current as HTMLDivElement)

        useEffect(() => {
            if (!enabled) {
                setTopGradientOpacity(0)
                setBottomGradientOpacity(1)
                return
            }

            const containerElement = scrollContainerRef.current
            if (!containerElement) {
                return
            }

            const updateGradientOpacity = () => {
                const distanceToBottom = Math.max(
                    containerElement.scrollHeight - containerElement.clientHeight - containerElement.scrollTop,
                    0
                )
                const topFadeRatio = Math.min(containerElement.scrollTop / gradientHeight, 1)
                const bottomFadeRatio = Math.min(distanceToBottom / gradientHeight, 1)

                setTopGradientOpacity(topFadeRatio)
                setBottomGradientOpacity(bottomFadeRatio)
            }

            updateGradientOpacity()
            containerElement.addEventListener('scroll', updateGradientOpacity, { passive: true })
            window.addEventListener('resize', updateGradientOpacity)

            const resizeObserver = new ResizeObserver(updateGradientOpacity)
            resizeObserver.observe(containerElement)

            if (containerElement.firstElementChild) {
                resizeObserver.observe(containerElement.firstElementChild)
            }

            return () => {
                containerElement.removeEventListener('scroll', updateGradientOpacity)
                window.removeEventListener('resize', updateGradientOpacity)
                resizeObserver.disconnect()
            }
        }, [enabled, gradientHeight])

        return (
            <div className={cn('relative', className)}>
                <div ref={scrollContainerRef} className={scrollContainerClassName} style={scrollContainerStyle}>
                    {children}
                </div>

                {enabled && (
                    <>
                        <div
                            className='absolute top-0 left-0 w-full pointer-events-none z-60'
                            style={{ height: `${gradientHeight}px`, opacity: topGradientOpacity }}
                        >
                            <div className='bg-gradient-to-b from-[#27272e] to-[rgba(39,39,46,0)] h-full w-full' />
                        </div>

                        <div
                            className='absolute bottom-0 left-0 w-full pointer-events-none z-60'
                            style={{ height: `${gradientHeight}px`, opacity: bottomGradientOpacity }}
                        >
                            <div className='bg-gradient-to-b to-[#27272e] from-[rgba(39,39,46,0)] h-full w-full' />
                        </div>
                    </>
                )}
            </div>
        )
    }
)

ScrollGradientBlock.displayName = 'ScrollGradientBlock'
